import { gql } from '@apollo/client'

import { Cookie, CookieNames } from '../cookie'
import { isMobileApp } from './helpers'
import config from '../config'
import { graphQLErrorMessage, graphQLErrorsOf } from './validation'
import { getClient } from './apollo-client'

const PARAM_NAMES: CookieNames[] = [
  'inviteUUID',
  'origin',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'tracking',
]

const GUEST_COOKIE_NAME = 'guest'
const INVITE_COOKIE_NAME = 'inviteUUID'

//
// GraphQL fragment used to normalize how we fetch
// authenticated user data (signins/signups).
//
export const authenticatedUserFragment = gql`
  fragment userFragment on User {
    id
    uuid
    email
    firstName
    middleName
    lastName
    variant
    photoUrl
    countFunds
    countContributions
    followedFunds {
      id
      handle
      uuid
    }
    funds {
      id
      handle
      uuid
    }
    gifts {
      id
      fund {
        id
        handle
        uuid
      }
    }
  }
`

export function clearOutRegistrationCookies() {
  for (const name of PARAM_NAMES) {
    Cookie.delete(name)
  }
}

export function setRegistrationAuthenticated({
  isGuest,
  done
}: {
  isGuest: boolean,
  done: () => void
}) {
  sessionStorage.justSignedUp = true
  clearOutRegistrationCookies()
  handleAuthorization()
    .then(() => {
      if (isGuest) {
        Cookie.cdSet(GUEST_COOKIE_NAME, 'yes', { domain: config.COOKIE_DOMAIN })
      } else {
        Cookie.delete(GUEST_COOKIE_NAME)
      }
      done()
    })
    .catch((error) => {
      console.error('Failed to establish registration session', error)
    })
}

export function setSignInAuthenticated(
  acceptManagerInvite: Function | null,
  done: () => void
) {
  sessionStorage.justSignedUp = false
  handleAuthorization()
    .then(async () => {
      if (acceptManagerInvite) {
        await acceptManagerIfInvited(acceptManagerInvite)
      }
      Cookie.delete(GUEST_COOKIE_NAME)
      done()
    })
    .catch((error) => {
      console.error('Failed to establish authenticated session', error)
    })
}

export async function acceptManagerIfInvited(mutation: Function) {
  const inviteUuid = Cookie.get(INVITE_COOKIE_NAME)

  if (inviteUuid) {
    try {
      const result = await mutation({ variables: { input: { inviteUuid } } })
      Cookie.delete(INVITE_COOKIE_NAME)
      return result
    } catch (error: any) {
      window.alert(graphQLErrorMessage(error))
    }
  }
}

async function handleAuthorization(): Promise<void> {
  const userJson = await fetchCurrentUser()

  if (!userJson) {
    return
  }

  // @ts-ignore
  const { firstName, lastName, email, uuid } = userJson
  const displayName = [firstName, lastName].filter(v => v).join(' ')

  // Identify user in FullStory
  if (config.env === 'production' && userJson) {
    const fullStory = (window as any).FS
    if (fullStory) {
      fullStory.identify(uuid, {
        displayName,
        appType: isMobileApp() ? 'mobile' : 'web',
        email,
      })
    }
  }
}

const CURRENT_USER_QUERY = gql`
  query CurrentUserForAuth {
    currentUser {
      ...userFragment
    }
  }
  ${authenticatedUserFragment}
`

const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout {
      success
    }
  }
`

async function fetchCurrentUser() {
  const client = getClient()
  const { data } = await client.query<any>({
    query: CURRENT_USER_QUERY,
    fetchPolicy: 'network-only',
  })

  return data?.currentUser || null
}

export async function clearServerSession() {
  // clean up cookies as we logout
  Cookie.delete('redirectTo')
  Cookie.delete('giftInProgress')

  const client = getClient()

  try {
    const { data } = await client.mutate<any>({
      mutation: LOGOUT_MUTATION,
      fetchPolicy: 'no-cache',
    })

    if (data?.logout?.success) {
      return
    }
  } catch (_error) {
    console.error('Failed to clear server session', _error)
    throw _error
  }
}

const authError = 'Authentication is required'
export function hasAuthenticationError(error: unknown) {
  return graphQLErrorsOf(error).some((e) => e.message === authError)
}
